import {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useContext,
} from "react";
import { CSVUploader, SimpleActionDialog } from "../../../components";
import { dataType } from "../../../components/GenericList/GenericList";
import ImportedDataList from "../../CreateEventFlow/ImportedDataList/ImportedDataList";
import { APIContext } from "../../../store/APIStore";
import { jobStatus } from "../../../dataProvider/interfaces/IEvent";
import EmptyFileWarning from "./EmptyFileWarning";
import  Box  from "@mui/material/Box";
import { theme } from "../../../theme";
import CSVConfigForm from "./CSVConfigForm";

export interface IProps {
  type: dataType;
  onCSVUpload: Function;
  eventId: number;
  eventSessionId?: string; // need this for reservations csv upload
}

const CSVUploadFlow = (
  { type, onCSVUpload, eventId, eventSessionId }: IProps,
  ref: any
) => {
  const [actionDialog, setActionDialog] = useState({
    open: false,
    title: "",
    loading: false,
    content: <></>,
    fullWidth: false,
    maxWidth:"",
  });

  const csvUploaderRef = useRef<any>();
  const api = useContext(APIContext);
  let jobChecker: any;

  useImperativeHandle(
    ref,
    () => ({
      openDialog() {
        showWarningDialog();
      },
    }),
    []
  );
  const showWarningDialog = () => {
    csvUploaderRef.current.showWarning();
  };

  const importData = async (importedData: any, fieldsMatch: any) => {
    if (type === dataType.SESSIONS) {
      importSessionsData(importedData, fieldsMatch);
    }
    if (type === dataType.RESERVATIONS) {
      importReservationsData(importedData, fieldsMatch);
    }

    if (type === dataType.ATTENDEES) {
      importAttendeesData(importedData, fieldsMatch);
    }
  };

  const importReservationsData = async (
    importedData: any,
    fieldsMatch: any
  ) => {
    if (!eventSessionId) {
      console.error("Uploading a list of session reservations but no session id was selected!");
      return
    }

    setActionDialog({
      open: true,
      title: "Importing reservations...",
      loading: true,
      fullWidth: false,
      content: <></>,
      maxWidth:"",
    });

    try {

      // upload the csv file and get a path
      const data = (await api.fileService.uploadCSVFile(
        eventId,
        importedData.file.name,
        importedData.file
      )) as any;

      // start a file processing job
      const jobData = (await api.sessionService.processSessionReservationsCSV(
        eventId,
        eventSessionId,
        data.filePath,
        fieldsMatch
      )) as any;

      // check job status
      jobChecker = setInterval(() => {
        checkJobStatus(jobData.job);
      }, 3000); // check for jobs status every 3s
    } catch (error: any) {
      console.error(error.response);
      setActionDialog({
        open: true,
        fullWidth: false,
        loading: false,
        content: (
          <Box color={theme.palette.error.main}>
            {error.response.data.message}
          </Box>
        ),
        title: "Import failure",
        maxWidth:"",
      });
    }
  };

  const importSessionsData = async (importedData: any, fieldsMatch: any) => {
    setActionDialog({
      open: true,
      title: "Importing sessions...",
      loading: true,
      fullWidth: false,
      content: <></>, 
      maxWidth:"",
    });

    try {
      // upload the csv file and get a path
      const data = (await api.fileService.uploadCSVFile(
        eventId,
        importedData.file.name,
        importedData.file
      )) as any;

      // start a file processing job
      const jobData = (await api.sessionService.processEventSessionsCSV(
        eventId,
        data.filePath,
        fieldsMatch
      )) as any;

      // check job status
      jobChecker = setInterval(() => {
        checkJobStatus(jobData.job);
      }, 3000); // check for jobs status every 3s
    } catch (error: any) {
      console.log(error.response);
      setActionDialog({
        open: true,
        fullWidth: false,
        loading: false,
        content: (
          <Box color={theme.palette.error.main}>
            {error.response.data.message}
          </Box>
        ),
        title: "Import failure",
        maxWidth:"",
      });
    }
  };

  const importAttendeesData = async (importedData: any, fieldsMatch: any) => {
    setActionDialog({
      open: true,
      title: "Importing attendees...",
      loading: true,
      fullWidth: false,
      content: <></>,
      maxWidth:"",
    });

    try {
      // upload the csv file and get a path
      const data = (await api.fileService.uploadCSVFile(
        eventId,
        importedData.file.name,
        importedData.file
      )) as any;

      // start a file processing job
      const jobData = (await api.attendeeService.processEventAttendeesCSV(
        eventId,
        data.filePath,
        fieldsMatch
      )) as any;

      // check job status
      jobChecker = setInterval(() => {
        checkJobStatus(jobData.job);
      }, 3000); // check for jobs status every 3s
    } catch (error: any) {
      console.log(error.response);
      setActionDialog({
        open: true,
        fullWidth: false,
        loading: false,
        content: (
          <Box color={theme.palette.error.main}>
            {error.response.data.message}
          </Box>
        ),
        title: "Import failure",
        maxWidth:"",
      });
    }
  };

  const checkJobStatus = async (jobId: number) => {
    try {
      const { status, result } = (await api.eventService.getEventJob(
        eventId,
        jobId
      )) as any; // get processing job status

      if (status === jobStatus.ERROR) {
        clearInterval(jobChecker);
        setActionDialog({
          open: true,
          fullWidth: false,
          loading: false,
          content: <Box color={theme.palette.error.main}>{result}</Box>,
          title: "Import failure",
          maxWidth:"",
        });
      }

      if (status === jobStatus.FINISHED) {
        clearInterval(jobChecker);
        setActionDialog({
          ...actionDialog,
          open: false,
        });
        onCSVUpload(status, result);
      }
    } catch (error: any) {
      console.log(error.response);
      onCSVUpload(jobStatus.ERROR, error.response.data.message);
    }
  };

  // the step where we preview the data mapping which will be imported
  const showFileDataList = (csvImport: any, fieldsMatch: any) => {
    setActionDialog({
      open: true,
      loading: false,
      title: "",
      fullWidth: true,
      maxWidth:"",
      content: (
        <ImportedDataList
          csvFieldsMatch={fieldsMatch}
          csvType={type}
          csvData={csvImport.data}
          onValidateData={() => {
            importData(csvImport, fieldsMatch);
          }}
          onRejectData={() => {
            showMappingForm(csvImport, fieldsMatch);
          }}
        />
      ),
    });
  };

  // the step where we show the csv map form
  // const showMappingForm = (csvImport: any, initialMap?: any) => {
  //   setActionDialog({
  //     open: true,
  //     loading: false,
  //     title: "",
  //     fullWidth: false,
  //     content: (
  //       <CSVMappingForm
  //         initialMap={initialMap}
  //         csvType={type}
  //         csvData={csvImport.data}
  //         onFormSubmit={(mapValues: any) => {
  //           showFileDataList(csvImport, mapValues);
  //         }}
  //         onCancel={() => {
  //           setActionDialog({
  //             ...actionDialog,
  //             open: false,
  //           });
  //           showWarningDialog();
  //         }}
  //       />
  //     ),
  //   });
  // };

  // the step where we show the csv map form
  const showMappingForm = (csvImport: any, initialMap?: any) => {
    setActionDialog({
      open: true,
      loading: false,
      title: "",
      fullWidth: true,
      maxWidth:"xl",
      content: (
        <CSVConfigForm
          initialMap={initialMap}
          csvType={type}
          csvData={csvImport.data}
          onFormSubmit={(mapValues: any) => {
            if (type !== dataType.ATTENDEES) showFileDataList(csvImport, mapValues);
            else importData(csvImport, mapValues);
          }}
          onCancel={() => {
            setActionDialog({
              ...actionDialog,
              open: false,
            });
            showWarningDialog();
          }}
        />
      ),
    });
  };

  // the step where we show a warning in case en empty csv was uploaded
  const showEmptyFileWarning = () => {
    setActionDialog({
      open: true,
      title: "",
      loading: false,
      fullWidth: false,
      maxWidth:"",
      content: (
        <EmptyFileWarning
          onConfirm={() => {
            setActionDialog({
              ...actionDialog,
              open: false,
            });
            showWarningDialog();
          }}
        />
      ),
    });
  };

  // close the dialog
  const closeDialog = () => {
    setActionDialog({
      open: false,
      content: <></>,
      title: "",
      loading: false,
      fullWidth: false,
      maxWidth:"",
    });
  };

  return (
    <>
      <CSVUploader
        ref={csvUploaderRef}
        onFileUpload={(data: any, fileInfo: any, file: File) => {
          if (data.length === 0) {
            showEmptyFileWarning();
          } else {
            showMappingForm({ data, fileInfo, file });
          }
        }}
        hideButton={true}
        type={type}
      />

      {actionDialog.open && (
        <SimpleActionDialog
          open={actionDialog.open}
          title={actionDialog.title}
          content={!actionDialog.loading ? actionDialog.content : ""}
          maxWidth="xl"
          fullWidth={actionDialog.fullWidth} // for certain steps we need full width dialog
          isLoading={actionDialog.loading}
          onClose={() => {
            closeDialog();
          }}
        />
      )}
    </>
  );
};

export default forwardRef(CSVUploadFlow);
